import React from 'react';
import { Space, Button, Card, Select, InputNumber, Slider, Popconfirm, Typography, Empty } from 'antd';
import { PlusOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { AudioAsset } from '../../../types';

const { Text } = Typography;

interface SfxTabPanelProps {
  selectedEditedPanel: any;
  selectedPanel: any;
  selectedPanelIndex: number;
  selectedSoundIndex: number | null;
  setSelectedSoundIndex: (index: number | null) => void;
  sfxList: AudioAsset[];
  handleAddSfx: (panelIndex: number) => void;
  handleEditSfx: (panelIndex: number, sfxIndex: number, updates: any) => void;
  handleDeleteSfx: (panelIndex: number, sfxIndex: number) => void;
  refreshAudioLibrary: () => void;
}

const SfxTabPanel: React.FC<SfxTabPanelProps> = ({
  selectedEditedPanel,
  selectedPanel,
  selectedPanelIndex,
  selectedSoundIndex,
  setSelectedSoundIndex,
  sfxList,
  handleAddSfx,
  handleEditSfx,
  handleDeleteSfx,
  refreshAudioLibrary,
}) => {
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          icon={<PlusOutlined />}
          onClick={() => handleAddSfx(selectedPanelIndex)}
          disabled={sfxList.length === 0}
          block
          size="small"
          style={{ flex: 1 }}
        >
          Add Sound Effect
        </Button>
        <Button
          type="default"
          size="small"
          icon={<ReloadOutlined />}
          onClick={() => refreshAudioLibrary()}
          style={{ color: '#1890ff' }}
        />
      </div>

      {selectedEditedPanel?.soundEffects && selectedEditedPanel.soundEffects.length > 0 ? (
        selectedEditedPanel.soundEffects.map((sfx: any, sfxIdx: number) => (
          <Card 
            key={sfxIdx} 
            size="small"
            id={`sound-effect-${sfxIdx}`}
            style={{ 
              fontSize: '12px',
              backgroundColor: selectedSoundIndex === sfxIdx ? '#6a5a4a' : '#3a3a3a',
              border: selectedSoundIndex === sfxIdx ? '2px solid #faad14' : '1px solid #444',
              transition: 'all 0.2s',
            }}
            styles={{ body: { backgroundColor: selectedSoundIndex === sfxIdx ? '#6a5a4a' : '#3a3a3a' } }}
            onClick={() => setSelectedSoundIndex(sfxIdx)}
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong style={{ fontSize: '12px', color: '#fff' }}>SFX #{sfxIdx + 1}</Text>
                <Popconfirm
                  title="Delete this SFX?"
                  onConfirm={() => handleDeleteSfx(selectedPanelIndex, sfxIdx)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </div>

              <Select
                value={sfx.audioId}
                onChange={(value) => handleEditSfx(selectedPanelIndex, sfxIdx, { audioId: value })}
                showSearch
                style={{ width: '100%' }}
                size="small"
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={sfxList.map((s: AudioAsset) => ({
                  value: s.id,
                  label: s.name
                }))}
              />

              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: '10px', display: 'block', color: '#999' }}>
                    Time (s):
                  </Text>
                  <InputNumber
                    size="small"
                    style={{ width: '100%' }}
                    min={0}
                    max={selectedPanel.endTime - selectedPanel.startTime || 10}
                    step={0.1}
                    value={sfx.triggerTime}
                    onChange={(value) => handleEditSfx(selectedPanelIndex, sfxIdx, { triggerTime: value })}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: '10px', display: 'block', color: '#999' }}>
                    Dur (s):
                  </Text>
                  <InputNumber
                    size="small"
                    style={{ width: '100%' }}
                    min={0.1}
                    max={10}
                    step={0.1}
                    value={sfx.duration || 1.5}
                    onChange={(value) => handleEditSfx(selectedPanelIndex, sfxIdx, { duration: value })}
                  />
                </div>
              </div>
              
              <div>
                <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
                  Volume:
                </Text>
                <Slider
                  min={0}
                  max={1}
                  step={0.05}
                  value={sfx.volume}
                  onChange={(value) => handleEditSfx(selectedPanelIndex, sfxIdx, { volume: value })}
                  marks={{
                    0: { label: <span style={{ color: '#999' }}>0</span> },
                    0.25: { label: <span style={{ color: '#999' }}>0.25</span> },
                    0.5: { label: <span style={{ color: '#999' }}>0.5</span> },
                    1: { label: <span style={{ color: '#999' }}>1</span> }
                  }}
                />
              </div>

              {sfx.reasoning && (
                <Text style={{ fontSize: '10px', color: '#aaa' }}>
                  💡 {sfx.reasoning}
                </Text>
              )}
            </Space>
          </Card>
        ))
      ) : (
        <Empty 
          description={<Text style={{ color: '#999' }}>No sound effects</Text>}
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      )}
    </Space>
  );
};

export default SfxTabPanel;
